import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import ArrowLeftIcon from './CustomIcons/ArrowLeftIcon';
import ForwardArrowIcon from './CustomIcons/ForwardArrowIcon';
import PlusIcon from './CustomIcons/PlusIcon';
import { CONTENT_INSET } from './PageLayout/constants';
import {
    CARD_FLARE_PX,
    CARD_FLARE_TIP_PX,
    CARD_WIDTH_PX,
    FLARE_CLASS,
    STRIP_RULE_PX,
} from './SelectableCard';
import { CardStripSlotContext, PINNED_SLOT, SCROLLED_SLOT } from './cardStripSlot';
import { FOCUS_RING } from './focusRing';
import { useAdoptedStyleSheet } from './useAdoptedStyleSheet';

export type CardStripAddTileEmphasis = 'primary' | 'secondary';

export interface CardStripAddTileProps {
    /** Visible label, e.g. "Add Profile". Also names the button for assistive tech. */
    label: string;
    /** Defaults to a plus. */
    icon?: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
    /** Leave unset: CardStrip picks `primary` while the strip is empty and `secondary` once cards exist. */
    emphasis?: CardStripAddTileEmphasis;
    className?: string;
}

/** Palette for the strip's rule. Pass the tone of whatever card is selected. */
export type CardStripTone = 'profile' | 'library' | 'editing';

export interface CardStripProps {
    /** The selected card's value, matched against each radio child's `data-value`. */
    value: string;
    /** Overrides the palette the strip reads off the selected card. See `CardStripTone`. */
    tone?: CardStripTone;
    onChange: (value: string) => void;
    /** Cards. Any element with `role="radio"` and a `data-value` joins the group; an
     *  add tile must be a direct child or it scrolls with the cards. */
    children: React.ReactNode;
    /** Pinned, non-scrolling slot before the cards (the Library card in the profile picker). */
    leading?: React.ReactNode;
    /** Circular scroll affordances, shown only on an edge that can actually scroll. */
    arrows?: boolean;
    /** Fade the cards out at any edge they scroll past. */
    fadeEdges?: boolean;
    /** Snap cards to the leading edge as the strip scrolls. */
    snap?: boolean;
    /** Run the bottom rule the full width of the page card while the cards stay on the gutter.
     *  Only has an effect inside a layout that publishes its inset (`PageLayout`). */
    bleed?: boolean;
    /** Whether a selection change scrolls the selected card into view. The strip's opening
     *  selection is placed instantly; later ones slide, unless motion is reduced. Set `false`
     *  when the value comes from a URL, or a deep-linked strip below the fold jumps the page
     *  on load. */
    scrollSelectedIntoView?: boolean;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    className?: string;
    style?: React.CSSProperties;
}

/** Arrow keys move AND select, per the WAI-ARIA radiogroup pattern. */
const NAV_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']);

const RADIO_SELECTOR = '[role="radio"]';
const SCROLLER_ATTR = 'data-card-strip-scroller';
const TROUGH_ATTR = 'data-card-strip-trough';
const ROOT_CLASS = 'malachite-card-strip';
const SCROLLER_CLASS = 'malachite-card-strip-scroller';
const FADE_CLASS = 'malachite-card-strip-fade';
const ADD_TILE_CLASS = 'malachite-card-strip-add-tile';

const ACTIONS_SELECTOR = '[data-selectable-card-actions]';
const FOCUSABLE_SELECTOR =
    'a[href], area[href], button, input, select, textarea, iframe, [contenteditable="true"], [tabindex]';
const PREV_TABINDEX_ATTR = 'data-card-strip-prev-tabindex';

const EDGE_EPSILON_PX = 1;
const SCROLL_STEP_RATIO = 0.8;

const scrollStepFor = (scroller: HTMLElement): number => {
    const [first, second] = Array.from(scroller.children);
    const pitch =
        first && second
            ? second.getBoundingClientRect().left - first.getBoundingClientRect().left
            : (first?.getBoundingClientRect().width ?? 0);
    // `clientWidth` counts the flare padding, which is not width a card can be read in.
    const visible = scroller.clientWidth - CARD_FLARE_PX * 2;

    if (pitch <= 0) {
        return visible * SCROLL_STEP_RATIO;
    }

    return Math.max(1, Math.floor(visible / pitch)) * pitch;
};
const FADE_WIDTH_PX = 80;

const ARROW_SIZE_PX = 20;
const ARROW_GLYPH_PX = 11;
const GUTTER_REST_PX = 12;
const GUTTER_OPEN_PX = 52;
const ARROW_OFFSET_PX = (GUTTER_OPEN_PX + ARROW_SIZE_PX) / 2;

const LIP_SHADOW = 'var(--malachite-shadow-default)';
const ARROW_BACKGROUND =
    'var(--malachite-card-strip-arrow-background, var(--malachite-card-background, #ffffff))';
const ARROW_BORDER = 'var(--malachite-card-strip-arrow-border, #e8e8e8)';
const TROUGH_BACKGROUND = 'var(--malachite-card-strip-background, #f1f1f1)';
const CARD_CORNER = 'var(--malachite-radius-xxl, 14px)';
const CARD_RADIUS = `${CARD_CORNER} ${CARD_CORNER} 0 0`;
const TROUGH_CORNER = 'var(--malachite-radius-xxxl, 16px)';
const TROUGH_RADIUS = `${TROUGH_CORNER} ${TROUGH_CORNER} 0 0`;
const TROUGH_SHOULDER_PX = 4;
const FADE_RAMP_PX = FADE_WIDTH_PX + (CARD_FLARE_PX - TROUGH_SHOULDER_PX);
const SLOT_WIDTH_PX = CARD_WIDTH_PX;
const DIVIDER_COLOR = 'var(--malachite-card-strip-divider, #dddddd)';
const FADE_COLOR = 'var(--malachite-card-strip-fade, transparent)';
const ADD_HOVER_BACKGROUND = 'var(--malachite-selectable-card-hover-background, #e8e8e8)';
const ADD_PILL_BORDER = 'var(--malachite-card-strip-arrow-border, #e8e8e8)';
const ADD_PILL_BORDER_ACTIVE = 'var(--malachite-selectable-card-hover-rule, #bbbbbb)';
const ADD_PILL_SHADOW_DEEP = '0px 1px 0px 0px rgba(0, 0, 0, 0.4)';
const ADD_PILL_BACKGROUND = 'var(--malachite-card-background, #ffffff)';
const ADD_PILL_FOREGROUND = 'var(--malachite-text-primary, #0a0a0a)';
const ADD_PILL_PRIMARY_BACKGROUND =
    'var(--malachite-card-strip-add-primary-background, var(--malachite-text-primary, #0a0a0a))';
const ADD_PILL_PRIMARY_FOREGROUND =
    'var(--malachite-card-strip-add-primary-foreground, var(--malachite-card-background, #ffffff))';
const ADD_PILL_PRIMARY_SHADOW = '0px 1px 0px 0px rgba(10, 10, 10, 0.7)';

const BOTTOM_RULE_WEIGHT_PX = STRIP_RULE_PX;

const OVERFLOW_INSET_PX = 16;

const CLIP_SLACK_PX = 9999;
const FOCUS_RING_REACH_PX = 4;

/** A sheet, not inline styles: these rules have to reach children this component does not render. */
const SCROLLER_CSS = `
.${ROOT_CLASS} {
    clip-path: inset(-${CLIP_SLACK_PX}px -${CLIP_SLACK_PX}px 0);
}
.${ROOT_CLASS}:has(:focus-visible) {
    clip-path: inset(-${CLIP_SLACK_PX}px -${CLIP_SLACK_PX}px -${FOCUS_RING_REACH_PX}px);
}
.${SCROLLER_CLASS} {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-behavior: smooth;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding-block: ${OVERFLOW_INSET_PX}px;
    margin-block: -${OVERFLOW_INSET_PX}px;
    /* overflow-x clips both axes, so the end cards would lose a flare; the negative margin trades
       the padding back out and scroll-padding keeps the rest position at zero. */
    padding-inline: ${CARD_FLARE_PX}px;
    margin-inline: -${CARD_FLARE_PX}px;
    scroll-padding-inline: ${CARD_FLARE_PX}px;
}
/* A card focused at a faded edge has to stay readable, so the fade lifts entirely. */
.${ROOT_CLASS}:has(:focus-visible) .${FADE_CLASS} {
    display: none;
}
.${SCROLLER_CLASS}::-webkit-scrollbar {
    display: none;
}
/* No bottom edge: the strip's rule is the baseline for the add tile as much as for a card. */
.${ADD_TILE_CLASS} > * {
    background: ${TROUGH_BACKGROUND};
    border-radius: ${CARD_RADIUS};
}
.${ADD_TILE_CLASS}:hover:not(:has(button:disabled)) > * {
    background: ${ADD_HOVER_BACKGROUND};
}
.${ADD_TILE_CLASS} button:active:not(:disabled) {
    opacity: 0.8;
}
.${ADD_TILE_CLASS}:has(button:active) > * {
    background: ${TROUGH_BACKGROUND};
}
.${ADD_TILE_CLASS}[data-emphasis='secondary'] button {
    border-color: ${ADD_PILL_BORDER};
    box-shadow: ${LIP_SHADOW};
}
.${ADD_TILE_CLASS}[data-emphasis='secondary']:hover:not(:has(button:disabled)) button {
    border-color: ${ADD_PILL_BORDER_ACTIVE};
    box-shadow: ${ADD_PILL_SHADOW_DEEP};
}
.${ADD_TILE_CLASS}[data-emphasis='secondary'] button:active:not(:disabled) {
    border-color: ${ADD_PILL_BORDER_ACTIVE};
    box-shadow: ${ADD_PILL_SHADOW_DEEP};
}
.${ADD_TILE_CLASS}[data-emphasis='primary'] button {
    border-color: transparent;
    box-shadow: ${ADD_PILL_PRIMARY_SHADOW};
}
.${SCROLLER_CLASS} > * {
    flex: 0 0 auto;
    min-width: 0;
}
.${SCROLLER_CLASS} > * + * {
    border-left: 1px solid ${DIVIDER_COLOR};
}
.${SCROLLER_CLASS}[data-snap='true'] {
    scroll-snap-type: x proximity;
}
.${SCROLLER_CLASS}[data-snap='true'] > * {
    scroll-snap-align: start;
}
@media (prefers-reduced-motion: reduce) {
    .${SCROLLER_CLASS} {
        scroll-behavior: auto;
    }
    .${SCROLLER_CLASS}[data-snap='true'] {
        scroll-snap-type: none;
    }
    .${SCROLLER_CLASS}[data-snap='true'] > * {
        scroll-snap-align: none;
    }
}`;

const STYLE_ID = 'malachite-card-strip-style';

/** `window.matchMedia` is absent under SSR and in older test environments. */
const prefersReducedMotion = (): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return true;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

const radioValue = (radio: HTMLElement): string | null =>
    radio.dataset.value ?? radio.getAttribute('value');

const isRadioDisabled = (radio: HTMLElement): boolean =>
    radio.hasAttribute('disabled') || radio.getAttribute('aria-disabled') === 'true';

const actionsFor = (radio: HTMLElement): HTMLElement | null => {
    const card = radio.parentElement;
    if (!card || card.querySelectorAll(RADIO_SELECTOR).length !== 1) {
        return null;
    }
    return card.querySelector<HTMLElement>(ACTIONS_SELECTOR);
};

/** A card is wider than its radio, so `closest` misses clicks on the count row. */
const radioForClick = (target: Element, root: Element): HTMLElement | null => {
    const direct = target.closest<HTMLElement>(RADIO_SELECTOR);
    if (direct) {
        return direct;
    }
    if (target.closest(ACTIONS_SELECTOR)) {
        return null;
    }
    let node: Element | null = target;
    while (node && root.contains(node)) {
        const radios: NodeListOf<HTMLElement> = node.querySelectorAll(RADIO_SELECTOR);
        if (radios.length === 1 && radios[0].parentElement === node) {
            return radios[0];
        }
        node = node.parentElement;
    }
    return null;
};

const actionControls = (radio: HTMLElement): HTMLElement[] => {
    const actions = actionsFor(radio);
    return actions ? Array.from(actions.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)) : [];
};

const suppressTabStop = (element: HTMLElement): void => {
    if (!element.hasAttribute(PREV_TABINDEX_ATTR)) {
        element.setAttribute(PREV_TABINDEX_ATTR, element.getAttribute('tabindex') ?? '');
    }
    element.setAttribute('tabindex', '-1');
};

const restoreTabStop = (element: HTMLElement): void => {
    if (!element.hasAttribute(PREV_TABINDEX_ATTR)) {
        return;
    }
    const previous = element.getAttribute(PREV_TABINDEX_ATTR);
    element.removeAttribute(PREV_TABINDEX_ATTR);
    if (previous) {
        element.setAttribute('tabindex', previous);
    } else {
        element.removeAttribute('tabindex');
    }
};

const TONE_RULE: Record<CardStripTone, string> = {
    profile: 'var(--malachite-selectable-card-selected-border, #047b43)',
    library: 'var(--malachite-selectable-card-library-selected-border, #bbbbbb)',
    editing: 'var(--malachite-selectable-card-pending-foreground, #96580a)',
};
const RULE_ROW_STYLE: React.CSSProperties = {
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'left bottom',
    backgroundSize: `100% ${BOTTOM_RULE_WEIGHT_PX}px`,
};

/** The rule runs under the flare's tip rather than butting against it, so antialiasing
 *  cannot open the junction into a gap. */
const RULE_GAP_OVERLAP_PX = 0.5;

const FLARE_TIP_INSET_PX = CARD_FLARE_PX - CARD_FLARE_TIP_PX;

const flareTips = (card: HTMLElement): { left: number; right: number } | null => {
    const flare = card.querySelector(`:scope > .${FLARE_CLASS}`);
    if (!flare) {
        return null;
    }
    const { left, right } = flare.getBoundingClientRect();
    return { left: left + FLARE_TIP_INSET_PX, right: right - FLARE_TIP_INSET_PX };
};

/** The rule is cut where the selected card stands, never covered: the strip's clip lands on the
 *  rule's own edge, so a cover takes the same partial alpha and the rule bleeds through it. */
const paintRule = (
    root: HTMLElement | null,
    scroller: HTMLElement | null,
    card: HTMLElement | null | undefined,
    tone: CardStripTone,
): void => {
    if (!root) {
        return;
    }
    const colour = TONE_RULE[tone];
    const rootRect = root.getBoundingClientRect();
    const rootLeft = rootRect.left;
    const clip = card && scroller?.contains(card) ? scroller.getBoundingClientRect() : rootRect;
    const cardRect = card?.getBoundingClientRect();
    const tips = card ? flareTips(card) : null;
    const tipLeft = tips?.left ?? (cardRect ? cardRect.left - CARD_FLARE_TIP_PX : 0);
    const tipRight = tips?.right ?? (cardRect ? cardRect.right + CARD_FLARE_TIP_PX : 0);
    const from = cardRect ? Math.max(tipLeft, clip.left) + RULE_GAP_OVERLAP_PX - rootLeft : 0;
    const to = cardRect ? Math.min(tipRight, clip.right) - RULE_GAP_OVERLAP_PX - rootLeft : 0;

    root.style.backgroundImage =
        to > from
            ? `linear-gradient(to right, ${colour} 0 ${from}px, transparent ${from}px ${to}px, ${colour} ${to}px 100%)`
            : `linear-gradient(to right, ${colour} 0 100%)`;
};

const BLEED_STYLE: React.CSSProperties = {
    marginInline: `calc(${CONTENT_INSET} * -1)`,
    paddingInline: CONTENT_INSET,
};

/** The rule's row is a margin, never padding: a background paints under its own padding, which
 *  would hide the very line the troughs stand on. */
const TROUGH_SURFACE: React.CSSProperties = {
    background: TROUGH_BACKGROUND,
    borderRadius: TROUGH_RADIUS,
    paddingTop: `${TROUGH_SHOULDER_PX}px`,
    paddingInline: `${TROUGH_SHOULDER_PX}px`,
    marginBottom: `${BOTTOM_RULE_WEIGHT_PX}px`,
};
const TROUGH_STYLE: React.CSSProperties = {
    ...TROUGH_SURFACE,
    marginInline: `${GUTTER_REST_PX}px`,
};
const TROUGH_GUTTER_STYLES: Record<string, React.CSSProperties> = Object.fromEntries(
    [GUTTER_REST_PX, GUTTER_OPEN_PX].flatMap((start) =>
        [GUTTER_REST_PX, GUTTER_OPEN_PX].map((end) => [
            `${start}:${end}`,
            { ...TROUGH_SURFACE, marginInline: `${start}px ${end}px` },
        ]),
    ),
);
const ADD_TILE_SHAPE_STYLE: React.CSSProperties = TROUGH_SURFACE;

const PINNED_SLOT_STYLE: React.CSSProperties = TROUGH_SURFACE;

const EMPTY_ADD_SLOT_STYLE: React.CSSProperties = {
    marginInlineStart: `${GUTTER_REST_PX}px`,
};

const ADD_PILL_FILL: Record<CardStripAddTileEmphasis, React.CSSProperties> = {
    primary: {
        backgroundColor: ADD_PILL_PRIMARY_BACKGROUND,
        color: ADD_PILL_PRIMARY_FOREGROUND,
    },
    secondary: { backgroundColor: ADD_PILL_BACKGROUND, color: ADD_PILL_FOREGROUND },
};

const AddTile: React.FC<CardStripAddTileProps> = ({
    label,
    icon,
    onClick,
    disabled = false,
    emphasis = 'secondary',
    className = '',
}) => (
    <div
        className={ADD_TILE_CLASS}
        data-emphasis={emphasis}
        style={{
            ...ADD_TILE_SHAPE_STYLE,
            width: `${SLOT_WIDTH_PX}px`,
            height: `calc(100% - ${BOTTOM_RULE_WEIGHT_PX}px)`,
        }}
    >
        <div className="flex h-full items-center justify-center px-4">
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                style={ADD_PILL_FILL[emphasis]}
                className={`${FOCUS_RING} inline-flex items-center justify-center gap-1.5 h-[var(--malachite-control-height-medium,32px)] w-[108px] px-2.5 rounded-xl border border-solid text-[length:var(--malachite-font-size-small,13px)] font-medium whitespace-nowrap ${
                    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                } ${className}`}
            >
                <span className="inline-flex h-[13px] w-[13px] shrink-0 items-center justify-center">
                    {icon ?? <PlusIcon size={13} />}
                </span>
                {label}
            </button>
        </div>
    </div>
);

const isAddTile = (node: React.ReactNode): node is React.ReactElement<CardStripAddTileProps> =>
    React.isValidElement(node) && node.type === AddTile;

const hidesAddTile = (child: React.ReactNode): boolean =>
    React.isValidElement<{ children?: React.ReactNode }>(child) &&
    child.type === React.Fragment &&
    React.Children.toArray(child.props.children).some(isAddTile);

interface ArrowButtonProps {
    side: 'left' | 'right';
    onClick: () => void;
}

const ArrowButton: React.FC<ArrowButtonProps> = ({ side, onClick }) => (
    <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        aria-label={side === 'left' ? 'Scroll left' : 'Scroll right'}
        onClick={onClick}
        className="absolute top-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center w-5 h-5 rounded-lg cursor-pointer text-[var(--malachite-text-secondary)]"
        style={{
            [side]: `-${ARROW_OFFSET_PX}px`,
            background: ARROW_BACKGROUND,
            border: `1px solid ${ARROW_BORDER}`,
            boxShadow: LIP_SHADOW,
        }}
    >
        {side === 'left' ? (
            <ArrowLeftIcon size={ARROW_GLYPH_PX} />
        ) : (
            <ForwardArrowIcon size={ARROW_GLYPH_PX} />
        )}
    </button>
);

/** A horizontally scrolling radiogroup of cards — the "pick one of these" strip. */
const CardStrip: React.FC<CardStripProps> & { AddTile: React.FC<CardStripAddTileProps> } = ({
    value,
    tone,
    onChange,
    children,
    leading,
    arrows = true,
    fadeEdges = true,
    snap = true,
    bleed = false,
    scrollSelectedIntoView = true,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    className = '',
    style,
}) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const scrollerRef = useRef<HTMLDivElement>(null);
    const hasPositioned = useRef(false);
    const [edges, setEdges] = useState({ canScrollLeft: false, canScrollRight: false });

    useAdoptedStyleSheet(rootRef, SCROLLER_CSS, STYLE_ID);

    const childList = React.Children.toArray(children);
    const cards = childList.filter((child) => !isAddTile(child));
    const wrappedAddTile = cards.some(hidesAddTile);
    const hasCards = cards.length > 0;

    const addTiles = childList
        .filter(isAddTile)
        .map((tile) =>
            tile.props.emphasis === undefined
                ? React.cloneElement(tile, { emphasis: hasCards ? 'secondary' : 'primary' })
                : tile,
        );

    const childCount = childList.length;

    const getRadios = useCallback((): HTMLElement[] => {
        const root = rootRef.current;
        if (!root) {
            return [];
        }
        return Array.from(root.querySelectorAll<HTMLElement>(RADIO_SELECTOR));
    }, []);

    const getNavigableRadios = useCallback(
        (): HTMLElement[] => getRadios().filter((radio) => radioValue(radio) !== null),
        [getRadios],
    );

    const warned = useRef(new Set<string>());
    const warnOnce = useCallback((key: string, message: string): void => {
        if (warned.current.has(key)) {
            return;
        }
        warned.current.add(key);
        console.warn(message);
    }, []);

    const [derivedTone, setDerivedTone] = useState<CardStripTone>('profile');

    useLayoutEffect(() => {
        const selectedCard = getRadios()
            .find((radio) => radioValue(radio) === value)
            ?.parentElement?.getAttribute('data-variant');
        if (
            selectedCard === 'profile' ||
            selectedCard === 'library' ||
            selectedCard === 'editing'
        ) {
            setDerivedTone(selectedCard);
        }
    }, [getRadios, value]);

    useLayoutEffect(() => {
        const radios = getRadios();
        const navigable = getNavigableRadios();
        const selected = navigable.find((radio) => radioValue(radio) === value);
        const tabStop = selected ?? navigable.find((radio) => !isRadioDisabled(radio)) ?? null;

        radios.forEach((radio) => {
            const holdsTabStop = radio === tabStop;
            const nextTabIndex = holdsTabStop ? 0 : -1;
            if (radio.tabIndex !== nextTabIndex) {
                radio.tabIndex = nextTabIndex;
            }
            for (const control of actionControls(radio)) {
                if (holdsTabStop) {
                    restoreTabStop(control);
                } else {
                    suppressTabStop(control);
                }
            }
        });

        if (process.env.NODE_ENV === 'production') {
            return;
        }

        if (navigable.length !== radios.length) {
            warnOnce(
                'missing-value',
                'CardStrip: a child with role="radio" has no `data-value`. It cannot be selected or reached by arrow keys, so it is excluded from the radio set. Give every card a `data-value`.',
            );
        }

        const values = new Set<string>();
        for (const radio of navigable) {
            const current = radioValue(radio);
            if (current === null) {
                continue;
            }
            if (values.has(current)) {
                warnOnce(
                    `duplicate:${current}`,
                    `CardStrip: two cards share the data-value "${current}". Both will read as checked and only the first is selectable — data-value must be unique.`,
                );
            }
            values.add(current);
        }

        const selectionSettled = value !== '';

        if (radios.length > 0 && selectionSettled && !values.has(value)) {
            warnOnce(
                `unmatched:${value}`,
                `CardStrip: value "${value}" matches no card's data-value, so the group reports no selection.`,
            );
        }

        const checked = radios.filter(
            (radio) => radio.getAttribute('aria-checked') === 'true',
        ).length;
        if (radios.length > 0 && selectionSettled && checked !== 1) {
            warnOnce(
                `checked:${checked}`,
                `CardStrip: ${checked} cards are aria-checked, but a radiogroup has exactly one. Drive each card's checked state from the strip's \`value\`.`,
            );
        }

        if (wrappedAddTile) {
            warnOnce(
                'wrapped-add-tile',
                'CardStrip: a CardStrip.AddTile inside a fragment is treated as a card and scrolls with them. The add tile must be a direct child of CardStrip.',
            );
        }
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: cards arriving is the signal
    useLayoutEffect(() => {
        if (!scrollSelectedIntoView) {
            return;
        }
        const scroller = scrollerRef.current;
        const selected = getRadios().find((radio) => radioValue(radio) === value);
        // jsdom implements neither, and a strip that cannot scroll has nothing to bring into view.
        if (!scroller || !selected || typeof scroller.scrollTo !== 'function') {
            return;
        }

        // Positioned rather than asked for: the track is scroll-snapped, and `scrollIntoView`
        // on a card several widths away lands back on the nearest snap point instead — so a
        // card appended past the end never came into view at all.
        // Measured against the scroller rather than an offset parent: the cards are flex
        // children whose `offsetParent` is not the scroller, so `offsetLeft` is not its frame.
        const card = (selected.closest('[role="radio"]') ?? selected) as HTMLElement;
        const cardRect = card.getBoundingClientRect();
        const viewRect = scroller.getBoundingClientRect();
        const left = scroller.scrollLeft + (cardRect.left - viewRect.left);
        const right = left + cardRect.width;
        const visibleFrom = scroller.scrollLeft + CARD_FLARE_PX;
        const visibleTo = scroller.scrollLeft + scroller.clientWidth - CARD_FLARE_PX;

        // The first placement is where the strip opens — a deep link, not a move — so it is
        // instant. Every later one is the selection travelling, and travel is legible only if
        // you can see it happen.
        const wasPositioned = hasPositioned.current;
        hasPositioned.current = true;

        if (left >= visibleFrom && right <= visibleTo) {
            return;
        }

        scroller.scrollTo({
            left:
                right > visibleTo
                    ? right - scroller.clientWidth + CARD_FLARE_PX
                    : left - CARD_FLARE_PX,
            behavior: wasPositioned && !prefersReducedMotion() ? 'smooth' : 'instant',
        });
    }, [getRadios, scrollSelectedIntoView, value, childCount]);

    const ruleTone = tone ?? derivedTone;
    const callerPaintsBackground =
        style?.background !== undefined || style?.backgroundImage !== undefined;

    const measure = useCallback(() => {
        const scroller = scrollerRef.current;
        if (!callerPaintsBackground) {
            paintRule(
                rootRef.current,
                scroller,
                getRadios().find((radio) => radioValue(radio) === value)?.parentElement,
                ruleTone,
            );
        }
        if (!scroller) {
            return;
        }
        const { scrollWidth, clientWidth, scrollLeft } = scroller;
        const overflows = scrollWidth > clientWidth + EDGE_EPSILON_PX;
        const next = {
            canScrollLeft: overflows && scrollLeft > EDGE_EPSILON_PX,
            canScrollRight: overflows && scrollLeft + clientWidth < scrollWidth - EDGE_EPSILON_PX,
        };
        setEdges((previous) =>
            previous.canScrollLeft === next.canScrollLeft &&
            previous.canScrollRight === next.canScrollRight
                ? previous
                : next,
        );
    }, [callerPaintsBackground, getRadios, ruleTone, value]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: adding or removing a card changes the scroll width, so re-measure on the count
    useLayoutEffect(() => {
        measure();

        const scroller = scrollerRef.current;
        if (!scroller) {
            return;
        }

        const onScroll = (): void => measure();
        scroller.addEventListener('scroll', onScroll, { passive: true });

        const observer =
            typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => measure()) : null;
        observer?.observe(scroller);

        return () => {
            scroller.removeEventListener('scroll', onScroll);
            observer?.disconnect();
        };
    }, [measure, childCount]);

    const scrollByStep = useCallback((direction: -1 | 1) => {
        const scroller = scrollerRef.current;
        if (!scroller || typeof scroller.scrollBy !== 'function') {
            return;
        }
        scroller.scrollBy({
            left: direction * scrollStepFor(scroller),
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
    }, []);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (!NAV_KEYS.has(event.key)) {
                return;
            }

            const target = event.target;
            if (!(target instanceof Element) || !target.closest(RADIO_SELECTOR)) {
                return;
            }

            const enabled = getNavigableRadios().filter((radio) => !isRadioDisabled(radio));
            if (enabled.length === 0) {
                return;
            }

            event.preventDefault();

            const currentPosition = enabled.findIndex((radio) => radioValue(radio) === value);
            let nextPosition: number;
            if (event.key === 'Home') {
                nextPosition = 0;
            } else if (event.key === 'End') {
                nextPosition = enabled.length - 1;
            } else {
                const delta = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
                const from = currentPosition === -1 ? 0 : currentPosition;
                nextPosition = (from + delta + enabled.length) % enabled.length;
            }

            const next = enabled[nextPosition];
            const nextValue = radioValue(next);
            if (nextValue !== null && nextValue !== value) {
                onChange(nextValue);
            }
            next.focus();
        },
        [getNavigableRadios, onChange, value],
    );

    const handleClick = useCallback(
        (event: React.MouseEvent) => {
            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }
            const root = rootRef.current;
            if (!root) {
                return;
            }
            const radio = radioForClick(target, root);
            if (!radio || isRadioDisabled(radio)) {
                return;
            }
            const clicked = radioValue(radio);
            if (clicked !== null && clicked !== value) {
                onChange(clicked);
            }
        },
        [onChange, value],
    );

    const showLeftArrow = arrows && edges.canScrollLeft;
    const showRightArrow = arrows && edges.canScrollRight;

    const fadeLeft = fadeEdges && edges.canScrollLeft;
    const fadeRight = fadeEdges && edges.canScrollRight;
    const troughStyle = arrows
        ? TROUGH_GUTTER_STYLES[
              `${showLeftArrow ? GUTTER_OPEN_PX : GUTTER_REST_PX}:${
                  showRightArrow ? GUTTER_OPEN_PX : GUTTER_REST_PX
              }`
          ]
        : TROUGH_STYLE;

    return (
        <div
            ref={rootRef}
            onKeyDown={handleKeyDown}
            onClick={handleClick}
            className={`${ROOT_CLASS} flex items-stretch ${className}`}
            style={{
                ...RULE_ROW_STYLE,
                ...(bleed ? BLEED_STYLE : null),
                ...style,
            }}
        >
            <div
                role="radiogroup"
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
                className={hasCards ? 'flex items-stretch min-w-0' : 'flex items-stretch'}
            >
                {leading ? (
                    <CardStripSlotContext.Provider value={PINNED_SLOT}>
                        <div className="shrink-0" style={PINNED_SLOT_STYLE}>
                            {leading}
                        </div>
                    </CardStripSlotContext.Provider>
                ) : null}
                {hasCards ? (
                    <div
                        {...{ [TROUGH_ATTR]: '' }}
                        className="relative min-w-0 shrink basis-auto"
                        style={troughStyle}
                    >
                        <div
                            ref={scrollerRef}
                            tabIndex={-1}
                            {...{ [SCROLLER_ATTR]: '' }}
                            data-snap={snap ? 'true' : 'false'}
                            className={SCROLLER_CLASS}
                        >
                            <CardStripSlotContext.Provider value={SCROLLED_SLOT}>
                                {cards}
                            </CardStripSlotContext.Provider>
                        </div>
                        {showLeftArrow ? (
                            <ArrowButton side="left" onClick={() => scrollByStep(-1)} />
                        ) : null}
                        {showRightArrow ? (
                            <ArrowButton side="right" onClick={() => scrollByStep(1)} />
                        ) : null}
                        {fadeLeft || fadeRight ? (
                            <div
                                aria-hidden
                                data-card-strip-fade=""
                                className={`${FADE_CLASS} absolute pointer-events-none`}
                                style={{
                                    inset: `-${TROUGH_SHOULDER_PX}px -${CARD_FLARE_PX}px 0`,
                                    background: `linear-gradient(to right, ${fadeLeft ? FADE_COLOR : 'transparent'} 0, transparent ${FADE_RAMP_PX}px, transparent calc(100% - ${FADE_RAMP_PX}px), ${fadeRight ? FADE_COLOR : 'transparent'} 100%)`,
                                }}
                            />
                        ) : null}
                    </div>
                ) : null}
            </div>
            {addTiles.length > 0 ? (
                <div
                    className="shrink-0"
                    style={!hasCards && leading ? EMPTY_ADD_SLOT_STYLE : undefined}
                >
                    {addTiles}
                </div>
            ) : null}
        </div>
    );
};

CardStrip.AddTile = AddTile;

export default CardStrip;
export { CardStrip };
